import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import './CourseList.css';

const CourseList = ({ addCourseToSchedule, selectedCourses, removeCourseFromSchedule, onCoursesLoaded }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedDescription, setSelectedDescription] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState('');
  const [showGradCourses, setShowGradCourses] = useState(false);

  // Clark University core requirements
  const coreRequirements = [
    { code: '', label: 'All Requirements' },
    { code: 'VE', label: 'Values Perspective (VE)' },
    { code: 'SP', label: 'Science Perspective (SP)' },
    { code: 'WE', label: 'Written Expression (WE)' },
    { code: 'FA', label: 'Formal Analysis (FA)' },
    { code: 'POP', label: 'Problems of Practice (POP)' },
    { code: 'GP', label: 'Global Perspective (GP)' },
    { code: 'HP', label: 'Historical Perspective (HP)' },
    { code: 'AP', label: 'Aesthetic Perspective (AP)' },
    { code: 'IP', label: 'Individual & Social Perspective (IP)' },
    { code: 'DI', label: 'Diversity & Inclusion (DI)' },
    { code: 'LP', label: 'Language Perspective (LP)' }
  ];

  // Load data directly from CSV
  useEffect(() => {
    console.log("Loading CSV data...");
    // Make sure the path to CSV works with GitHub Pages
    const csvFilePath = `${process.env.PUBLIC_URL}/fall_2026_course_schedule.csv`;
   
    console.log("Fetching CSV from path:", csvFilePath);
    
    // Function to process array/object strings in CSV
    const processArraysAndObjects = (value) => {
      if (value === 'NaN' || value === '') return null;
      
      try {
        // If it looks like an array, parse it
        if (value && typeof value === 'string') {
          if (value.startsWith('[') || value.startsWith('(')) {
            return JSON.parse(value.replace(/'/g, '"'));
          }
        }
        return value;
      } catch (e) {
        console.error("Error processing value:", value, e);
        return value;
      }
    };
    
    fetch(csvFilePath)
      .then(response => {
        if (!response.ok) {
          console.error(`Failed to fetch CSV file: ${response.status} ${response.statusText}`);
          throw new Error(`Failed to fetch CSV file: ${response.status} ${response.statusText}`);
        }
        return response.text();
      })
      .then(csvData => {
        Papa.parse(csvData, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete: (result) => {
            console.log("Parsed CSV data:", result);
            // Process the parsed data to handle arrays and objects
            const processedData = result.data.map(row => {
              const processedRow = {};
              Object.keys(row).forEach(key => {
                processedRow[key] = processArraysAndObjects(row[key]);
              });
              return processedRow;
            });
            
            console.log("Processed data first item:", processedData[0]);
            
            // Log additional sample courses to check for requirement tags
            console.log("Sample courses to check for requirements:");
            for (let i = 0; i < Math.min(5, processedData.length); i++) {
              console.log(`Course ${i}:`, processedData[i]);
              console.log(`Course ${i} keys:`, Object.keys(processedData[i]));
            }
            
            setCourses(processedData);
            
            // Pass the courses to App.js
            if (onCoursesLoaded) {
              onCoursesLoaded(processedData);
            }
            
            // Extract unique departments for filtering
            const depts = [...new Set(processedData.map(course => {
              if (!course.Course) return null;
              const dept = course.Course.split(' ')[0];
              return dept || null;
            }).filter(Boolean))].sort();
            
            setLoading(false);
          },
          error: (error) => {
            console.error("Error parsing CSV:", error);
            setError('Error loading course data. Please try again later.');
            setLoading(false);
          }
        });
      })
      .catch(error => {
        console.error("Error fetching CSV:", error);
        setError(`Error loading course data: ${error.message}. Please check the console for more details and refresh the page.`);
        setLoading(false);
      });
  }, [onCoursesLoaded]);

  // Get unique departments
  const departments = [...new Set(courses.map(course => {
    if (!course.Course) return null;
    const dept = course.Course.split(' ')[0];
    return dept || null;
  }).filter(Boolean))].sort();

  const hasRequirementTag = (course, requirementCode) => {
    if (!requirementCode) return true;
    if (course.Attributes && course.Attributes.includes(requirementCode)) return true;
    return Boolean(course.Notes && course.Notes.includes(requirementCode));
  };

  // Filter courses based on search term, department, and requirement
  const filteredCourses = courses.filter(course => {
    if (!course.Course) return false;
    
    // Match search term
    const matchesSearch = course.Course.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (course.Title && course.Title.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Match department
    const matchesDepartment = selectedDepartment === "" || selectedDepartment === "All" || 
                             course.Course.startsWith(selectedDepartment);
    
    // Match requirement - Check in both Requirements field and Notes field since requirements might be stored there
    const matchesRequirement = hasRequirementTag(course, selectedRequirement);
    
    // Graduate course filter (courses numbered 300 or above)
    let matchesGradFilter = true;
    const courseParts = course.Course.split(' ');
    if (courseParts.length >= 2) {
      const courseNumber = parseInt(courseParts[1]);
      if (!isNaN(courseNumber) && courseNumber >= 300) {
        // If it's a graduate course, only show if checkbox is checked
        matchesGradFilter = showGradCourses;
      }
    }
    
    return matchesSearch && matchesDepartment && matchesRequirement && matchesGradFilter;
  });

  const handleAddCourse = (course) => {
    // Check if course is already selected, if so, remove it
    if (isCourseSelected(course)) {
      console.log("Removing course from schedule:", course);
      // Find the course in selectedCourses to get its ID
      const selectedCourse = selectedCourses.find(selected => selected.CRN === course.CRN);
      if (selectedCourse) {
        removeCourseFromSchedule(selectedCourse);
      }
      return;
    }
    
    console.log("Adding course to schedule:", course);
    
    // Make sure course data is properly formatted for the schedule
    const formattedCourse = {
      ...course,
      // Ensure Days is an array
      Days: Array.isArray(course.Days) ? course.Days : 
            (course.Days ? [course.Days] : []),
    };
    
    // Format the Times data correctly
    if (course.Times) {
      if (Array.isArray(course.Times)) {
        // If it's already an array, make sure each element is properly formatted
        if (course.Times.length === 2 && typeof course.Times[0] === 'string' && typeof course.Times[1] === 'string') {
          // If it's in the format ['0800', '0915'], keep it as is
          formattedCourse.Times = course.Times;
        } else if (course.Times.some(time => typeof time === 'string' && time.includes('-'))) {
          // If any element contains a dash (e.g. ['8:00-9:15']), split it
          formattedCourse.Times = course.Times.map(time => {
            if (typeof time === 'string' && time.includes('-')) {
              return time.split('-').map(t => t.trim());
            }
            return time;
          });
        }
      } else if (typeof course.Times === 'string') {
        // If it's a string, check if it contains a dash
        if (course.Times.includes('-')) {
          // Split the string on the dash and create an array with start/end times
          formattedCourse.Times = course.Times.split('-').map(t => t.trim());
        } else {
          // If no dash, just put the string in an array
          formattedCourse.Times = [course.Times];
        }
      }
    } else {
      // Default empty array if no times
      formattedCourse.Times = [];
    }
    
    console.log("Formatted course for schedule:", formattedCourse);
    addCourseToSchedule(formattedCourse);
  };

  const isCourseSelected = (course) => {
    return selectedCourses.some(selected => 
      selected.CRN === course.CRN
    );
  };

  const handleInfoClick = (course) => {
    setSelectedCourse(course);
  };

  const handleDescClick = (course) => {
    setSelectedDescription(course);
  };

  const handleCloseModal = () => {
    setSelectedCourse(null);
    setSelectedDescription(null);
  };

  if (loading) return <div className="loading">Loading courses from CSV file...</div>;
  if (error) return <div className="error">
    <h3>Error Loading Data</h3>
    <p>{error}</p>
    <p>If you're seeing this message, the CSV data file may not be loading correctly.</p>
  </div>;

  return (
    <div className="course-list-container">
      <div className="filters">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-row">
          <div className="department-filter">
            <label>Department</label>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          <div className="requirement-filter">
            <label>Core Requirement</label>
            <select
              value={selectedRequirement}
              onChange={(e) => setSelectedRequirement(e.target.value)}
            >
              {coreRequirements.map(req => (
                <option key={req.code} value={req.code}>{req.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grad-filter">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showGradCourses}
              onChange={(e) => setShowGradCourses(e.target.checked)}
            />
            Show Graduate Courses (300+)
          </label>
        </div>
      </div>

      <div className="course-count">
        {filteredCourses.length} courses found
      </div>

      <div className="courses-grid">
        {filteredCourses.map(course => (
          <div key={`${course.Course}-${course.Section}`} className="course-card">
            <div className="course-header">
              <div className="course-header-content">
                <h3>{course.Course}</h3>
                <p className="course-title">{course.Title}</p>
                <p className="course-section">Section {course.Section}</p>
                {course.Attributes && (
                  <div className="course-requirements">{course.Attributes}</div>
                )}
                {!course.Attributes && course.Notes && course.Notes.match(/(VE|WE|SP|FA|POP|GP|HP|AP|IP|DI|LP)/) && (
                  <div className="course-requirements">
                    {coreRequirements
                      .filter(req => req.code && course.Notes.includes(req.code))
                      .map(req => req.code)
                      .join(', ')}
                  </div>
                )}
              </div>
              <div className="course-card-actions">
                <button 
                  className="info-icon"
                  onClick={() => handleInfoClick(course)}
                  aria-label="View course details"
                >
                  Info
                </button>
                <button 
                  className="info-icon"
                  onClick={() => handleDescClick(course)}
                  aria-label="View course description"
                >
                  Desc
                </button>
              </div>
            </div>
            <div className="course-details">
              <p className="course-instructor"><strong>Professor:</strong> {course.Instructor || 'TBA'}</p>
              <button
                className={`add-button ${isCourseSelected(course) ? 'added' : ''}`}
                onClick={() => handleAddCourse(course)}
              >
                {isCourseSelected(course) ? 'Remove' : 'Add to Schedule'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedCourse && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedCourse.Course} - Section {selectedCourse.Section}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Title:</strong> {selectedCourse.Title}</p>
              <p><strong>Professor:</strong> {selectedCourse.Instructor || 'TBA'}</p>
              <p><strong>Location:</strong> {Array.isArray(selectedCourse.Location) ? selectedCourse.Location.join(', ') : (selectedCourse.Location || 'TBA')}</p>
              <p><strong>Days:</strong> {Array.isArray(selectedCourse.Days) ? selectedCourse.Days.join(', ') : selectedCourse.Days}</p>
              <p><strong>Times:</strong> {Array.isArray(selectedCourse.Times) ? 
                selectedCourse.Times.map(time => Array.isArray(time) ? `${time[0]} - ${time[1]}` : time).join(' | ') : 
                selectedCourse.Times || 'TBA'}
              </p>
              <p><strong>Credits:</strong> {selectedCourse.Units || 'TBA'}</p>
              {selectedCourse.Attributes && (
                <p><strong>Core Requirements:</strong> {selectedCourse.Attributes}</p>
              )}
              {!selectedCourse.Attributes && selectedCourse.Notes && 
                selectedCourse.Notes.match(/(VE|WE|SP|FA|POP|GP|HP|AP|IP|DI|LP)/) && (
                <p><strong>Core Requirements:</strong> {
                  coreRequirements
                    .filter(req => req.code && selectedCourse.Notes.includes(req.code))
                    .map(req => `${req.code} - ${req.label.replace(` (${req.code})`, '')}`)
                    .join(', ')
                }</p>
              )}
              <p><strong>Description:</strong> {selectedCourse.descriptions || "No description available."}</p>
              {selectedCourse.Notes && <p><strong>Notes:</strong> {selectedCourse.Notes}</p>}
            </div>
          </div>
        </div>
      )}

      {selectedDescription && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedDescription.Course} - {selectedDescription.Title}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Description:</strong> {selectedDescription.descriptions || "No description available."}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseList;
